import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';

/**
 * Formats a file size given in kilobytes into a locale-aware human-readable
 * string with a translated unit label.
 *
 * @example
 * 512     → "512 KB"   (en) | "٥١٢ ك.ب" (ar)
 * 2048    → "2.0 MB"   (en) | "٢٫٠ م.ب" (ar)
 */
@Pipe({ name: 'fileSize', pure: false })
export class FileSizePipe implements PipeTransform {
  readonly #locale = inject(TranslocoLocaleService);
  readonly #transloco = inject(TranslocoService);

  transform(sizeInKb: number): string {
    if (!sizeInKb || sizeInKb < 1) return '—';

    if (sizeInKb < 1024) {
      const formattedSize = this.#locale.localizeNumber(Math.round(sizeInKb), 'decimal');
      return `${formattedSize}\u202F${this.#transloco.translate('FILE.SIZE_KB')}`;
    }

    const n = this.#locale.localizeNumber(sizeInKb / 1024, 'decimal', undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return `${n}\u202F${this.#transloco.translate('FILE.SIZE_MB')}`;
  }
}
